<template>
  <div class="dynamic-form-fields">
    <b-row v-for="question in questions" :key="question.id">
      <b-col cols="12" sm="10">
        <ValidationProvider
          :name="question.question"
          :rules="question.required ? 'required' : ''"
          v-slot="{ errors }"
        >
          <b-form-group
            :label-for="`field-${question.id}`"
            label-align="left"
          >
            <template v-slot:label>
              {{ question.question }}
              <span v-if="question.required" class="text-danger">*</span>
            </template>

            <!-- Dropdown -->
            <b-form-select
              v-if="question.type === 'dropdown'"
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              :options="getSelectOptions(question)"
              :class="['form-control', errors[0] ? 'is-invalid' : '']"
              @change="updateAnswer(question.id)"
            >
              <template #first>
                <b-form-select-option :value="null" disabled>
                  {{ $t('choose') || 'Kies een optie' }}
                </b-form-select-option>
              </template>
            </b-form-select>

            <!-- Radio -->
            <b-form-radio-group
              v-else-if="question.type === 'radio'"
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              :options="getSelectOptions(question)"
              :class="[errors[0] ? 'is-invalid' : '']"
              stacked
              @change="updateAnswer(question.id)"
            />

            <!-- Checkbox -->
            <b-form-checkbox-group
              v-else-if="question.type === 'checkbox'"
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              :options="getSelectOptions(question)"
              :class="[errors[0] ? 'is-invalid' : '']"
              stacked
              @change="updateAnswer(question.id)"
            />

            <!-- Date -->
            <b-form-input
              v-else-if="question.type === 'date'"
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              type="date"
              :class="['form-control', errors[0] ? 'is-invalid' : '']"
              @input="updateAnswer(question.id)"
            />

            <!-- Textarea -->
            <b-form-textarea
              v-else-if="question.type === 'textarea'"
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              rows="3"
              :class="['form-control', errors[0] ? 'is-invalid' : '']"
              @input="updateAnswer(question.id)"
            />

            <!-- Input (default) -->
            <b-form-input
              v-else
              :id="`field-${question.id}`"
              v-model="answers[question.id]"
              :class="['form-control', errors[0] ? 'is-invalid' : '']"
              @input="updateAnswer(question.id)"
            />

            <div class="float-left text-danger">
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate/dist/vee-validate.full.esm';

export default {
  name: 'DynamicFormFields',
  components: {
    ValidationProvider
  },
  props: {
    questions: {
      type: Array,
      required: true,
      default: () => []
    },
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      answers: {}
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        // Convert from storage format { id: { question: "...", answer: "..." } }
        // to component format { id: "answer" }
        if (newVal && typeof newVal === 'object') {
          const flatAnswers = {}
          Object.keys(newVal).forEach(key => {
            if (newVal[key] && typeof newVal[key] === 'object' && 'answer' in newVal[key]) {
              // New format
              flatAnswers[key] = newVal[key].answer
            } else {
              // Old format (for backwards compatibility during transition)
              flatAnswers[key] = newVal[key]
            }
          })
          this.answers = flatAnswers
        } else {
          this.answers = {}
        }
      }
    }
  },
  methods: {
    getSelectOptions(question) {
      if (!question.options) return []
      return question.options.map(opt => ({
        value: opt.value,
        text: opt.label
      }))
    },
    updateAnswer(questionId) {
      // Transform answers to new format: { id: { question: "...", answer: "...", trigger_attention: bool } }
      const transformedAnswers = {}

      this.questions.forEach(q => {
        if (this.answers[q.id] !== undefined && this.answers[q.id] !== null && this.answers[q.id] !== '') {
          let triggerAttention = q.trigger_attention || false

          // Check if specific option triggers attention (for dropdowns, radio, checkbox)
          if (q.options && Array.isArray(q.options)) {
            if (Array.isArray(this.answers[q.id])) {
              // Checkbox - check if any selected option triggers attention
              triggerAttention = this.answers[q.id].some(value => {
                const option = q.options.find(opt => opt.value === value)
                return option && option.trigger_attention
              })
            } else {
              // Dropdown or radio - check if selected option triggers attention
              const selectedOption = q.options.find(opt => opt.value === this.answers[q.id])
              if (selectedOption && selectedOption.trigger_attention) {
                triggerAttention = true
              }
            }
          }

          transformedAnswers[q.id] = {
            question: q.question,
            answer: this.answers[q.id],
            trigger_attention: triggerAttention
          }
        }
      })

      this.$emit('input', transformedAnswers)
    }
  }
}
</script>

<style scoped>
.dynamic-form-fields {
  margin-top: 1rem;
}
</style>

